import React, { useState, useEffect, useMemo, useCallback } from "react";
import InitBookLogic from "./Book.logic";
import { FaClock, FaUsers, FaCalendarTimes, FaUser } from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./Book.module.css";
import Loading from "../Loading/Loading.component";
import { formatBookingDate as formatDate, formatLongDate, formatBookingTime as formatTime, bookingDayKey, calendarDayKey } from "../../helpers/dateFormat";

const BookComponent: React.FC = () => {
    const { selectedDate, client, bookings, locations, clients, selectedClient, data, isLoading, setSelectedDate, onBook, onCancel, onChangeLocation, selectClient, removeSelectedClient, onChange } = InitBookLogic();
    const [filteredBookings, setFilteredBookings] = useState<any[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
    const [repeatDays, setRepeatDays] = useState<{ [key: string]: boolean }>({});
    const [showModal, setShowModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
    const [trial, setTrial] = useState(false);

    const dayMappingAlwaysDisplayed: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri", string> = useMemo(() => ({
        Mon: "Pon",
        Tue: "Uto",
        Wed: "Sre",
        Thu: "Cet",
        Fri: "Pet",
    }), []);

    const dayMapping: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun", string> = useMemo(() => ({
        Mon: "Pon",
        Tue: "Uto",
        Wed: "Sre",
        Thu: "Cet",
        Fri: "Pet",
        Sat: "Sub",
        Sun: "Ned"
    }), []);

    const { availableDatesSet, clientBookingsDatesSet } = useMemo(() => {
        const availableDatesSet = new Set(
            bookings
                .filter((b) => !b.cancelled && (b.clients?.length || 0) < b.maxNumberOfClients)
                .map((b) => bookingDayKey(b.date))
        );

        const clientBookingsDatesSet = new Set(
            client?.bookings?.map((b: any) => bookingDayKey(b.date))
        );

        return { availableDatesSet, clientBookingsDatesSet };
    }, [bookings, client?.bookings]);

    const tileClassName = useCallback(({ date }: any) => {
        const formattedDate = calendarDayKey(date);

        // Proveri da li je datum selektovan i postavi plavu pozadinu
        if (selectedDate && formattedDate === calendarDayKey(selectedDate)) {
            return null; // Dodaj klasu za selektovani datum
        }

        if (clientBookingsDatesSet.has(formattedDate)) {
            return styles.orangeHighlightedDate; // Style for client bookings
        }
        if (availableDatesSet.has(formattedDate)) {
            return styles.highlightedDate; // Style for general available bookings
        }
        return null;
    }, [availableDatesSet, clientBookingsDatesSet, selectedDate]);

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        if (!client) {
            setFilteredBookings([]);
            return;
        }
        const selectedDateString = calendarDayKey(date);

        // Get client-specific bookings for the selected date, sorted by date and time
        const clientBookingsForDate = client.bookings?.filter((b: any) => bookingDayKey(b.date) === selectedDateString)
            .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];

        // Get general bookings for the selected date, excluding those that are in client.bookings
        const generalBookings = bookings
            .filter((b) =>
                bookingDayKey(b.date) === selectedDateString &&
                !(client.bookings?.some((cb: any) => cb.id === b.id))
            );

        // Combine client-specific and general bookings
        setFilteredBookings([...clientBookingsForDate, ...generalBookings]);
    };

    // Kada se promeni klijent ili lista termina, ponovo filtriraj za trenutno selektovan datum
    useEffect(() => {
        if (!selectedDate) return;
        if (!client) {
            setFilteredBookings([]);
            return;
        }
        const selectedDateString = calendarDayKey(selectedDate);
        const clientBookingsForDate = client.bookings?.filter((b: any) => bookingDayKey(b.date) === selectedDateString)
            .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
        const generalBookings = bookings
            .filter((b) =>
                bookingDayKey(b.date) === selectedDateString &&
                !(client.bookings?.some((cb: any) => cb.id === b.id))
            );
        setFilteredBookings([...clientBookingsForDate, ...generalBookings]);
    }, [client, bookings, selectedDate]);


    const handleBookingClick = (booking: any) => {
        setSelectedBooking(booking);
        // const selectedDayEng = new Date(booking.date).toLocaleDateString("en-US", {
        //     weekday: "short",
        // }) as keyof typeof dayMapping;
        // const selectedDay = dayMapping[selectedDayEng];
        // setRepeatDays((prev) => ({ ...prev, [selectedDay]: true }));
        setRepeatDays((prev) => ({ ...prev }));
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedBooking(null);
        setRepeatDays({});
        setTrial(false);
    };

    const shouldShowSaturday = () => {
        if (selectedBooking) {
            const bookingDate = new Date(selectedBooking.date);
            const dayOfWeek = bookingDate.getUTCDay(); // 0 = Sunday, 6 = Saturday

            // Proveri da li je subota
            return dayOfWeek === 6;
        }
        return false;
    };


    const formatDateString = (date: Date) => formatLongDate(date);

    const handleDayToggle = (day: string) => {
        setRepeatDays((prevState) => ({
            ...prevState,
            [day]: !prevState[day],
        }));
    };


    useEffect(() => {
        if (selectedBooking) {
            // const selectedDayEng = new Date(selectedBooking.date).toLocaleDateString("en-US", {
            //     weekday: "short",
            // }) as keyof typeof dayMapping;
            // const selectedDay = dayMapping[selectedDayEng];
            /* setRepeatDays((prevState) => ({
                ...prevState,
                [selectedDay]: true,
            })); */
            setRepeatDays((prevState) => ({
                ...prevState,
            }));
        }
    }, [selectedBooking, dayMapping]);

    const handleCancelClick = (id: number) => {
        setSelectedBookingId(id);
        setShowModal(true);
    };

    const confirmCancel = () => {
        if (selectedBookingId !== null) {
            onCancel(selectedBookingId);
            setSelectedDate(null)
        }
        setShowModal(false);
    };

    const closeModalCancel = () => setShowModal(false);

    return (
        <div className={styles.container}>
            {/* Modal for Confirmation */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <p>Da li ste sigurni da želite da otkažete ovaj trening?</p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '20px'
                        }}>
                            <button onClick={closeModalCancel} className="btn-secondary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Ne</button>
                            <button onClick={confirmCancel} className="btn-primary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Da</button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.padding50}>
                <label htmlFor="location">Lokacija</label>
                <select id="location" aria-label={"Izaberi lokaciju"} onChange={onChangeLocation} name="location">
                    {locations.map((l: string, index: number) => (
                        <option key={index} value={l}>{l}</option>
                    ))}
                </select>
            </div>
            {isLoading ? (
                <Loading />
            ) : (<>
                <div className={styles.calendarContainer}>
                    <Calendar
                        onClickDay={handleDateClick}
                        tileClassName={tileClassName}
                        view="month"
                        minDetail="month"
                        locale="sr-Latn"
                        minDate={new Date()}
                        className={styles.reactCalendar}
                    />
                </div>

                <br />

                {selectedDate && (
                    <div className={styles.selectedDateContainer}>
                        <h3>{formatDateString(selectedDate)}</h3>

                        {!client ? (
                            <div className={styles.noBookingsContainer}>
                                <FaCalendarTimes className={styles.noBookingsIcon} />
                                <h3 className={styles.noBookingsMessage}>Nije izabran nijedan klijent za zakazivanje termina</h3>
                            </div>
                        ) : filteredBookings.length > 0 ? (
                            <div className={styles.bookingsList}>
                                {filteredBookings.map((b) => {
                                    const isClientBooking = client?.bookings ? client.bookings.some((cb: any) => cb.id === b.id) : false;

                                    return (
                                        <div key={b.id} className={styles.bookingItem}>
                                            <div className={styles.bookingTime}>
                                                <FaClock className={styles.icon} />
                                                {formatTime(b.date)}
                                            </div>
                                            <div className={styles.bookingDetails}>
                                                <h4>{b.name}</h4>
                                                <p className={styles.bookingClientCount}>
                                                    <FaUsers className={styles.icon} />
                                                    Zauzeto: {b.clients ? b.clients.length : 0}/{b.maxNumberOfClients}
                                                </p>
                                                {b.user && (
                                                    <p className={styles.bookingClientCount}>
                                                        <FaUser className={styles.icon} />
                                                        Trener: {b.user.firstName} {b.user.lastName}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent parent onClick event if necessary
                                                    isClientBooking ? handleCancelClick(b.id) : handleBookingClick(b); // Toggle based on booking type
                                                }}
                                                className={styles.scheduleButton}
                                                style={isClientBooking ? { backgroundColor: 'var(--danger)', color: 'var(--white)' } : {}}
                                            >
                                                {isClientBooking ? "Otkaži" : "Zakaži"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.noBookingsContainer}>
                                <FaCalendarTimes className={styles.noBookingsIcon} />
                                <h3 className={styles.noBookingsMessage}>Nema aktivnosti za izabrani datum</h3>
                                <p className={styles.noBookingsSubtext}>Za tvoj program nema treninga na ovaj dan</p>
                            </div>
                        )}
                    </div>
                )}

                {modalVisible && selectedBooking && (
                    <div className={styles.modalOverlay} onClick={closeModal}>
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            {/* Podebljane su samo labele, vrednosti normalne */}
                            <p style={{ fontWeight: 400 }}><b>Lokacija:</b> {selectedBooking.location}</p>
                            <p style={{ fontWeight: 400 }}><b>Trening:</b> {selectedBooking.name}</p>
                            <p style={{ fontWeight: 400 }}><b>Datum:</b> {formatDate(selectedBooking.date)}</p>
                            <p style={{ fontWeight: 400 }}><b>Vreme:</b> {formatTime(selectedBooking.date)}</p>

                            <h4>Trening se ponavlja danima:</h4>
                            <div className={styles.repeatDaysContainer}>
                                {Object.keys(dayMappingAlwaysDisplayed).map((dayEng) => {
                                    const daySrpski = dayMapping[dayEng as keyof typeof dayMapping];
                                    return (
                                        <div key={daySrpski} className={styles.repeatDay}>
                                            <input
                                                type="checkbox"
                                                checked={repeatDays[daySrpski] || false}
                                                disabled={trial}
                                                onChange={() => handleDayToggle(daySrpski)}
                                            />
                                            {daySrpski}
                                        </div>
                                    );
                                })}
                                {shouldShowSaturday() && (
                                    <div key="Sub" className={styles.repeatDay}>
                                        <input
                                            type="checkbox"
                                            checked={repeatDays["Sub"] || false}
                                            disabled={trial}
                                            onChange={() => handleDayToggle("Sub")}
                                        />
                                        <label>Sub</label>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: 12, textAlign: "left" }}>
                                <label style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 8, cursor: "pointer", width: "fit-content" }}>
                                    <input
                                        type="checkbox"
                                        style={{ width: "auto", margin: 0 }}
                                        checked={trial}
                                        onChange={(e) => {
                                            setTrial(e.target.checked);
                                            // Probni je uvek samo jedan termin
                                            if (e.target.checked) setRepeatDays({});
                                        }}
                                    />
                                    <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Probni trening</span>
                                </label>
                                {trial && (
                                    <p style={{ fontSize: 12, opacity: 0.7, margin: "6px 0 0" }}>
                                        Probni trening se zakazuje samo za izabrani termin, bez ponavljanja.
                                    </p>
                                )}
                            </div>

                            <div className={styles.modalButtons}>
                                <button className={styles.saveButton} onClick={() => { onBook({ id: selectedBooking.id, repeatDays, trial }); setSelectedDate(null); closeModal() }}>
                                    Sačuvaj
                                </button>
                                <button className={styles.cancelButton} onClick={closeModal}>
                                    Odustani
                                </button>
                            </div>
                        </div>
                    </div>
                )
                }

                <h1 className="text-align-center" style={{marginTop: 24}}>Klijenti</h1>

                <div style={{display: "flex", flexDirection: "column", gap: "12px", padding: "0 20px"}}>
                    <div>
                        <label htmlFor="client">Pretraga klijenata</label>
                        <input placeholder="Traži po imenu ili prezimenu..." id="client" type={"text"} value={data.client} onChange={onChange} name="client"></input>
                    </div>
                </div>

                {selectedClient !== undefined && (
                    <div className="card" style={{ margin: "16px 20px" }}>
                        <button className="btn-secondary" type="button" onClick={() => removeSelectedClient(selectedClient)}>{selectedClient.firstName} {selectedClient.lastName}</button>
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: "0 20px", marginTop: "12px" }}>
                    {clients.map((c) => c.firstName.concat(" ", c.lastName).toLowerCase().replaceAll("ć", "c").replaceAll("č", "c").replaceAll("š", "s").replaceAll("ž", "z").replaceAll("đ", "dj").includes(data.client.toLowerCase().replaceAll("ć", "c").replaceAll("č", "c").replaceAll("š", "s").replaceAll("ž", "z").replaceAll("đ", "dj")) ? (
                        <button type="button" key={c.id} onClick={() => selectClient(c)}>{c.firstName} {c.lastName}</button>
                    ) : "")}
                </div>
            </>
            )
            }
        </div >
    );
};

export default BookComponent;
